// THE CLIENT SIDE OF THE REVIEW QUEUE (BUILD-BRIEF §4).
//
// Every call goes through `api/client.ts`; the row types come from `src/shared/types.ts`,
// which both sides read. The interfaces below EXTEND those types rather than restating them:
// what the queue adds is context (the subject's name, the one flag, the collision lane), never
// a second version of a `proposals` row.
//
// Vocabulary, everywhere and without exception: unheld · hold · held · amended · refused.
import { qs, request } from './client.js';
import type { ListResponse, Proposal, ProposalKind, RefuseReason } from '../../shared/types.js';

/** The six reasons a refusal may carry. The only signal a run ever gets back (DESIGN §8). */
export const REFUSE_REASONS: RefuseReason[] = [
  'duplicate', 'wrong_evidence', 'stale', 'not_relevant', 'out_of_scope', 'other',
];

/** English label per reason — the i18n key, translated at the call site with `t()`. */
export const REFUSE_REASON_LABEL: Record<RefuseReason, string> = {
  duplicate: 'duplicate',
  wrong_evidence: 'wrong evidence',
  stale: 'stale',
  not_relevant: 'not relevant',
  out_of_scope: 'out of scope',
  other: 'other',
};

/** The rail's ONE flag per row. `null` is a clean row, not an unchecked one. */
export type QueueFlag = 'collides' | 'no_evidence' | 'not_on_record' | null;

/**
 * One organisation proposal a claim names by ref — a dependency the CHAINED HOLD (round 6)
 * resolves before it applies the claim itself. `status` is the dependency's OWN status, not the
 * dependent's: `'unheld'` is the only one that still needs a click of its own, because the
 * server holds it first and folds the write into the same transaction.
 */
export interface ProposalDependency {
  id: number;
  ref: string;
  kind: 'organisation';
  name: string | null;
  status: 'unheld' | 'held' | 'amended' | 'refused';
}

/** A proposal as the queue lists it: the row, plus the names and the flag the rail draws. */
export interface QueueItem extends Proposal {
  subject_label: string | null;
  subject_ref: string | null;
  /** The names behind `payload.person_id` / `payload.organisation_id`, when it names them. */
  payload_person_name: string | null;
  payload_person_ref: string | null;
  payload_organisation_name: string | null;
  payload_organisation_ref: string | null;
  flag: QueueFlag;
  evidence_count: number;
  /** The organisation proposals this claim names by a `*_proposal_ref` (or an id that is in
   *  fact a proposal's) — only the unheld ones need holding, and the server chains them. Absent
   *  or empty means this claim names none, or names none still pending. */
  depends_on?: ProposalDependency[] | null;
  /** Set only on an `organisation` proposal: how many unheld proposals name it as their
   *  dependency right now — "N proposals wait on this". */
  dependents_count?: number | null;
}

/** One row of the record that the click would land beside — the collision lane. */
export interface Collision {
  kind: string;
  ref: string | null;
  label: string;
  detail: string | null;
  /** ▪ an operator holds it · ◇ it is itself only proposed. */
  held: boolean;
}

/**
 * One validation line. The SERVER names what it found; the PAGE says it in the operator's
 * language — which is why this is a code and not a sentence.
 */
export interface Check {
  level: 'pass' | 'warn';
  code:
    | 'evidence_present' | 'no_evidence' | 'evidence_cited' | 'evidence_uncited'
    | 'payload_complete' | 'payload_incomplete'
    | 'applier_registered' | 'no_applier'
    | 'collision_clean' | 'collides' | 'collision_unchecked'
    | string;
  detail?: string | null;
}

/** GET /api/proposals/:id — everything the stand needs before the operator commits. */
export interface ProposalStand {
  proposal: QueueItem;
  collisions: Collision[];
  /** FALSE means the check did not run. A missing check is never a passed check. */
  collisions_checked: boolean;
  checks: Check[];
  missing: string[];
  /** The ref a hold will mint, advisory exactly like the counter it reads. */
  next_ref: string | null;
  subject_label: string | null;
}

/**
 * GET /api/proposals/summary — the five rail cells.
 *
 * `edit_distance` and `hot_run` are `null` when NOTHING PRODUCED THEM: no amendment has been
 * made, no run has filed anything. The rail renders null as `fv--unknown` "not collected".
 */
export interface ReviewSummary {
  unheld: number;
  oldest_unheld: string | null;
  unheld_principals: number;
  held_7d: number;
  amended_7d: number;
  refused_7d: number;
  /** Everything already disposed of, all time — the TREATED tab's own count. */
  treated: number;
  by_kind: Partial<Record<ProposalKind, number>>;
  by_principal: Record<string, number>;
  refused_by_reason_7d: Partial<Record<RefuseReason, number>>;
  edit_distance: number | null;
  hot_run: {
    run_id: number;
    principal: string;
    filed: number;
    refused: number;
    unheld: number;
    pushed_back: number;
  } | null;
}

/** One line of the session ledger — what this operator disposed of, and what it wrote. */
export interface SessionEntry {
  proposal_id: number;
  ref: string;
  kind: ProposalKind;
  verdict: 'held' | 'amended' | 'refused';
  applied_ref: string | null;
  refuse_reason: RefuseReason | null;
  reviewer_note: string | null;
  subject_label: string | null;
  payload: Record<string, unknown>;
  at: string;
  has_record: boolean;
  /** False once it has been walked back: the row stays, its verb goes. */
  undoable: boolean;
  /** The CHAINED HOLD (round 6): the organisation proposals this one click held FIRST, before
   *  applying the claim itself. The undo below walks back THIS row only — the ledger line says
   *  so beside them, because a hold the operator cannot see is a hold he cannot answer for. */
  also_held?: Array<{ id: number; ref: string; kind: ProposalKind; applied_ref: string | null }>;
}

export interface ListFilters {
  status?: string | null;
  kind?: string | null;
  principal?: string | null;
  q?: string | null;
  limit?: number | null;
}

export interface HoldResult {
  proposal: QueueItem;
  applied_ref: string | null;
  /** False when this session cannot walk the hold back (nothing recorded what it wrote). */
  undoable: boolean;
  /**
   * The CHAINED HOLD (round 6): every organisation dependency this click held FIRST, in the
   * same transaction, before applying the claim itself. Undoing the dependent never undoes
   * these — the ledger says so beside each one.
   */
  also_held?: Array<{ id: number; ref: string; kind: ProposalKind; applied_ref: string | null }>;
}

/**
 * The answer to POST /proposals/hold-group — "Hold all cells" (BUILD-BRIEF-R3 §3).
 * `collided` is not a failure: those cells carry a differing current value and stay for a
 * per-cell decision, because superseding a held value is never a side effect of a bulk verb.
 */
export interface HoldGroupResult {
  held: Array<{ id: number; ref: string; key: string; applied_ref: string | null; undoable: boolean }>;
  collided: Array<{ id: number; ref: string; key: string; current_value: string | null; proposed_value: string | null }>;
}

/**
 * The answer to POST /proposals/hold-all — the mast verb (2026-09-06). One call holds for a
 * slice of the server's time and says what it did not reach as `remaining`; the page calls again
 * until that is 0. `left` is not a failure: the record already answers those rows, and the
 * reason is in words on each. `not_a_candidate` is what the filters matched that a bulk verb
 * never touches — a source's rows (their own Hold all) and the judgement kinds.
 */
export interface HoldAllResult {
  considered: number;
  held: Array<{ id: number; ref: string; kind: ProposalKind; applied_ref: string | null; undoable: boolean }>;
  left: Array<{ id: number; ref: string; kind: ProposalKind; why: string; guard: string }>;
  /** Held although the rule objected — only ever non-empty when `include_answered` was asked for. */
  overruled: Array<{ id: number; ref: string; kind: ProposalKind; why: string; guard: string }>;
  failed: Array<{ id: number; ref: string; kind: ProposalKind; error: string }>;
  remaining: number;
  /** Where the slice stopped. PASS IT BACK on the next call: it is what makes the second call do
   *  different work from the first. Null means the list was walked to its end. */
  next_cursor: { rank: number; id: number } | null;
  not_a_candidate: { sources: number; other_kinds: number };
}

/**
 * THE STANDING WAIVER (09.09.2026, «I need an auto accept feature»).
 *
 * Six classes of judgement stop a hold, and they are not equally serious. One click that held all
 * of them would, among 2'146 rows, let a machine reading overwrite two values the operator typed
 * himself — and a bulk press is the one thing that cannot show him which two. So he waives a
 * CLASS: the rule stops asking that question, of the backlog and of every new arrival.
 */
export interface GuardSwitch {
  guard: string;
  waived: boolean;
  /** How many unheld rows this class is holding back right now. Read live: it moves by tens an hour. */
  count: number;
  /** What waiving it costs, in his terms. Rendered beside the switch, never behind it. */
  cost: string;
}

export interface AutoHoldSettings {
  unheld: number;
  /** Rows no guard stops today — what a plain Hold all would write. */
  holdable: number;
  waived: string[];
  guards: GuardSwitch[];
  /** Guarded by something no switch reaches: a mislabelled birth date, an unreadable filing. */
  never_waivable: Array<{ guard: string; count: number; cost: string }>;
}

export const proposalsApi = {
  list: (filters: ListFilters = {}): Promise<ListResponse<QueueItem>> =>
    request<ListResponse<QueueItem>>(`/proposals${qs({ ...filters })}`),

  get: (id: number): Promise<ProposalStand> => request<ProposalStand>(`/proposals/${id}`),

  summary: (): Promise<ReviewSummary> => request<ReviewSummary>('/proposals/summary'),

  session: (): Promise<ListResponse<SessionEntry>> =>
    request<ListResponse<SessionEntry>>('/proposals/session'),

  /** The click. One transaction: the applier's writes and the status change commit together. */
  hold: (id: number): Promise<HoldResult> =>
    request<HoldResult>(`/proposals/${id}/hold`, { method: 'POST' }),

  /** The same click over a corrected payload — recorded as `amended`, never as `held`. */
  amend: (id: number, payload: Record<string, unknown>): Promise<HoldResult> =>
    request<HoldResult>(`/proposals/${id}/amend`, { method: 'POST', body: JSON.stringify({ payload }) }),

  refuse: (id: number, reason: RefuseReason, note?: string | null): Promise<{ proposal: QueueItem }> =>
    request<{ proposal: QueueItem }>(`/proposals/${id}/refuse`, {
      method: 'POST',
      body: JSON.stringify({ reason, note: note ?? null }),
    }),

  /**
   * Within this session only, and only from what the applier itself recorded. `note` is set
   * when the hold had CHAINED into an organisation: that organisation stays held, and the
   * sentence says which one, because the undo walks back one row and no more.
   */
  undo: (id: number): Promise<{ proposal: QueueItem; undone: string; note: string | null }> =>
    request<{ proposal: QueueItem; undone: string; note: string | null }>(`/proposals/${id}/undo`, { method: 'POST' }),

  /** The quiet verb: a filing that should never have been made. Never for a held one. */
  remove: (id: number): Promise<{ deleted: boolean; ref: string }> =>
    request<{ deleted: boolean; ref: string }>(`/proposals/${id}`, { method: 'DELETE' }),

  /** One decision over a run's raster cells — one transaction, one ledger entry per cell. */
  holdGroup: (run_id: number, subject_kind: 'person' | 'organisation', subject_id: number): Promise<HoldGroupResult> =>
    request<HoldGroupResult>('/proposals/hold-group', {
      method: 'POST',
      body: JSON.stringify({ run_id, subject_kind, subject_id }),
    }),

  /** The mast verb: every unheld row the list's filters match, across every page, one slice per call. */
  holdAll: (filters: { kind?: string | null; principal?: string | null; q?: string | null; include_answered?: boolean; cursor?: { rank: number; id: number } | null }): Promise<HoldAllResult> =>
    request<HoldAllResult>('/proposals/hold-all', {
      method: 'POST',
      body: JSON.stringify({
        kind: filters.kind ?? null, principal: filters.principal ?? null, q: filters.q ?? null,
        // The operator's override: hold the rows the rule guards too (08.09.2026).
        include_answered: filters.include_answered === true,
        // Where the last slice stopped (09.09.2026) — without it the next call redoes this one.
        cursor: filters.cursor ?? null,
      }),
    }),

  /** The six waivable guards, what each costs and how many rows it holds back RIGHT NOW. The rule
   *  is asked of every unheld row, so this is a few seconds of real work: call it on a click. */
  autoHold: (): Promise<AutoHoldSettings> => request<AutoHoldSettings>('/proposals/auto-hold'),

  /** Set the standing waiver. Honoured by Hold all AND on arrival, which is what stops the queue
   *  rebuilding. An unknown class is refused, never silently dropped. */
  setAutoHold: (waived: string[]): Promise<{ waived: string[] }> =>
    request<{ waived: string[] }>('/proposals/auto-hold', { method: 'PUT', body: JSON.stringify({ waived }) }),

  /** Walk a group hold back within this session, from the ledger it wrote. */
  undoGroup: (proposal_ids: number[]): Promise<{ undone: string[] }> =>
    request<{ undone: string[] }>('/proposals/hold-group/undo', {
      method: 'POST',
      body: JSON.stringify({ proposal_ids }),
    }),
};
